import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CheckCircle2, Zap, Clock, TrendingUp, MessageSquare, Sparkles } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Zap className="h-6 w-6" />,
      title: 'Otomatik Cevaplama',
      description: 'AI ile müşteri sorularını saniyeler içinde yanıtlayın. Ürün bilgilerine dayalı akıllı yanıtlar.',
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: '7/24 Aktif Destek',
      description: 'Arka planda çalışan sistem, gelen soruları 5 dakikada bir kontrol eder ve işler.',
    },
    {
      icon: <MessageSquare className="h-6 w-6" />,
      title: 'Hibrit veya Tam Oto',
      description: 'Hibrit modda onaylayın, Full-Auto modda otomatik gönderin. Kontrol tamamen sizde.',
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: 'İstatistikler & Raporlar',
      description: 'Soru hacmi, yanıt oranı ve performans metriklerinizi canlı takip edin.',
    },
    {
      icon: <CheckCircle2 className="h-6 w-6" />,
      title: 'Kolay Entegrasyon',
      description: 'Trendyol API anahtarlarınızı girin, özel promptunuzu yazın ve başlayın.',
    },
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: 'Akıllı Öğrenme',
      description: 'Özel promptlarla markanıza özel, profesyonel ve yardımsever ton yakalayan yanıtlar.',
    },
  ];

  const faqs = [
    {
      question: 'Trendyol API anahtarlarımı nasıl alırım?',
      answer: 'Trendyol Satıcı Paneli\'nden API ayarlarına giderek Seller ID, API Key ve API Secret bilgilerinizi alabilirsiniz. Bu bilgileri platformumuzdaki Ayarlar sayfasından girebilirsiniz.',
    },
    {
      question: 'Hibrit mod nedir?',
      answer: 'Hibrit modda, AI tarafından oluşturulan yanıtlar size gösterilir. Onaylandıktan sonra Trendyol\'a gönderilir. Bu sayede tam kontrol sizde olur.',
    },
    {
      question: 'Full-Auto mod güvenli mi?',
      answer: 'Evet! AI modelimiz ürün bilgilerine dayanarak yanıt verir. Ayrıca özel promptlar ile markanıza özel ton yakalayabilirsiniz. Ancak ilk başta Hibrit mod ile test etmenizi öneririz.',
    },
    {
      question: 'Ücretsiz deneme nasıl çalışır?',
      answer: 'Kaydınızı tamamladıktan sonra 10 soru hakkıyla başlanır. Bu sürede platformu test edebilir, ardından size uygun planı seçebilirsiniz.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Navbar */}
      <PublicHeader />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-[radial-gradient(120%_120%_at_80%_-10%,#FFF3E6_0%,#FFFFFF_60%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-orange-50 text-[#FF6000] hover:bg-orange-100 border-orange-200">
                Yeni: AI Destekli Müşteri Desteği
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                E-Ticaret Mağazanızı
                <span className="text-[#FF6000]"> AI ile Güçlendirin</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Trendyol ve Hepsiburada'daki müşteri sorularını yapay zeka ile otomatik yanıtlayın. Hibrit veya tam otomatik modda çalışan akıllı asistanınızla satışlarınızı artırın.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  size="lg"
                  className="bg-[#FF6000] hover:bg-orange-600 text-white"
                  onClick={() => navigate('/checkout')}
                  data-testid="hero-start-button"
                >
                  Hemen Başla
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-300 hover:bg-slate-50"
                  data-testid="hero-demo-button"
                >
                  Demo İzle
                </Button>
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span>Ücretsiz deneme</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span>Kredi kartı gerektirmez</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1683549004262-fcb3e5918bcf?q=85&w=800"
                alt="Trendyol seller working with AI"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Güçlü Özellikler</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              E-ticaret mağaza yönetiminizi kolaylaştıran akıllı araçlar
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center text-[#FF6000] mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing - Credit System */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-100 text-green-700 border-green-200">
              Yeni üyelere 100 kredi hediye
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="text-[#FF6000]">Aylık Sabit Ücret Yok!</span>
            </h2>
            <p className="text-xl text-slate-600 mb-2">
              Sadece kullandığınız kadar ödeyin
            </p>
            <p className="text-lg text-slate-500">
              Kredi satın alın, istediğiniz zaman kullanın
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-[#FF6000] shadow-2xl">
              <CardContent className="p-12">
                <div className="grid md:grid-cols-3 gap-8 mb-8">
                  <div className="text-center">
                    <Zap className="h-12 w-12 mx-auto mb-3 text-[#FF6000]" />
                    <div className="text-4xl font-bold text-[#FF6000] mb-2">1 Kredi</div>
                    <div className="text-slate-600">0.14-0.20 ₺</div>
                  </div>
                  <div className="text-center">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 text-blue-600" />
                    <div className="text-4xl font-bold text-blue-600 mb-2">1 Soru</div>
                    <div className="text-slate-600">10 kredi = 1.4-2 ₺</div>
                  </div>
                  <div className="text-center">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-600" />
                    <div className="text-4xl font-bold text-green-600 mb-2">Süresiz</div>
                    <div className="text-slate-600">Sınırsız geçerlilik</div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-center mb-4">Neler Dahil?</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span>Gelişmiş Merkur AI</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span>Hibrit & Full-Auto Mod</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span>Sınırsız Mağaza</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span>Trendyol & Hepsiburada</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={() => navigate('/register')}
                    className="flex-1 h-14 text-lg bg-[#FF6000] hover:bg-orange-600"
                  >
                    Ücretsiz Başla (100 Kredi Hediye)
                  </Button>
                  <Button
                    onClick={() => navigate('/pricing')}
                    variant="outline"
                    className="flex-1 h-14 text-lg border-[#FF6000] text-[#FF6000] hover:bg-orange-50"
                  >
                    Fiyatları İncele
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Sıkça Sorulan Sorular</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#FF6000] to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Şimdi Başlayın, Müşterilerinizi Mutlu Edin
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            İlk 10 soru ücretsiz. Kredi kartı gerektirmez.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#FF6000] hover:bg-slate-50"
            onClick={() => navigate('/checkout')}
            data-testid="cta-register-button"
          >
            Planları İncele
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
